import { useEffect, useRef, useState, type ReactElement } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button, CardSurface, Dialog, Notice } from '@/components';
import { useInterfaceLanguage } from '@/lib/interface-language';

export type AccountLifecycleAction = 'deactivate' | 'delete';

export interface AccountLifecycleSectionProps {
  busy?: { deactivate?: boolean; delete?: boolean };
  error?: { deactivate?: string; delete?: string };
  onDeactivateAccount: () => void;
  onDeleteAccount: () => void;
}

export interface AccountLifecycleDialogProps {
  action: AccountLifecycleAction | null;
  busy?: boolean;
  error?: string | null;
  onConfirm: (action: AccountLifecycleAction) => void;
  onCancel: () => void;
}

export function AccountLifecycleSection({
  busy,
  error,
  onDeactivateAccount,
  onDeleteAccount,
}: AccountLifecycleSectionProps): ReactElement {
  /*
   * FE-80.1 — BOTH exported components in this file call the hook for
   * themselves rather than the section threading labels down as props.
   * `AccountLifecycleDialog` is re-exported from `components/index.ts` and
   * is already rendered directly, without the section around it, by its own
   * test file; a future caller reaching it through the barrel would get an
   * English dialog with nothing red, because label props would have to be
   * optional to keep the section's own call site short and an omitted optional
   * prop type-checks. The hook is a context read, not a data hook.
   */
  const { t } = useInterfaceLanguage();
  const [action, setAction] = useState<AccountLifecycleAction | null>(null);
  const activeBusy = action === 'delete' ? busy?.delete : busy?.deactivate;
  const activeError = action === 'delete' ? error?.delete : error?.deactivate;

  return (
    <section aria-labelledby="account-lifecycle-heading" className="flex flex-col gap-3">
      <h2 id="account-lifecycle-heading" className="text-base font-semibold text-ink">
        {t('profile.lifecycle.heading')}
      </h2>
      <CardSurface variant="sheet" className="flex flex-col gap-3 p-4">
        <p className="text-sm text-ink-muted">{t('profile.lifecycle.body')}</p>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            busy={busy?.deactivate ?? false}
            onClick={() => setAction('deactivate')}
          >
            {t('profile.lifecycle.deactivate')}
          </Button>
          <Button
            variant="danger"
            busy={busy?.delete ?? false}
            onClick={() => setAction('delete')}
          >
            {t('profile.lifecycle.delete')}
          </Button>
        </div>
      </CardSurface>
      <AccountLifecycleDialog
        action={action}
        busy={activeBusy ?? false}
        error={activeError ?? null}
        onConfirm={(confirmedAction) => {
          if (confirmedAction === 'delete') {
            onDeleteAccount();
            return;
          }
          onDeactivateAccount();
        }}
        onCancel={() => setAction(null)}
      />
    </section>
  );
}

export function AccountLifecycleDialog({
  action,
  busy = false,
  error = null,
  onConfirm,
  onCancel,
}: AccountLifecycleDialogProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const open = action !== null;
  const isDelete = action === 'delete';
  // The same two keys the section's triggers read — one key, two call sites.
  // The confirm button and the control that opened the dialog must agree, and
  // a second pair of keys is a second place for them to stop agreeing.
  const actionLabel = isDelete
    ? t('profile.lifecycle.delete')
    : t('profile.lifecycle.deactivate');

  useEffect(() => {
    if (!open) setAcknowledged(false);
  }, [open, action]);

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onCancel();
      }}
      title={
        isDelete
          ? t('profile.lifecycle.deleteTitle')
          : t('profile.lifecycle.deactivateTitle')
      }
      description={t('profile.lifecycle.dialogDescription')}
      size="sm"
      initialFocus={cancelRef}
      footer={
        <>
          <Button
            variant="danger"
            busy={busy}
            disabled={!acknowledged}
            disabledReason={t('profile.lifecycle.acknowledgeReason')}
            onClick={() => {
              if (action !== null) onConfirm(action);
            }}
          >
            {actionLabel}
          </Button>
          <Button ref={cancelRef} variant="quiet" onClick={onCancel}>
            {t('profile.lifecycle.cancel')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Notice
          tone="warn"
          icon={<AlertTriangle className="size-4" />}
          title={
            isDelete
              ? t('profile.lifecycle.deleteNoticeTitle')
              : t('profile.lifecycle.deactivateNoticeTitle')
          }
          body={t('profile.lifecycle.consequences')}
        />
        <Notice tone="locked" body={t('profile.lifecycle.adminRule')} />
        {error !== null ? <Notice tone="bad" body={error} /> : null}
        <label className="flex gap-2 rounded-card border border-border-strong bg-surface-raised p-3 text-sm text-ink">
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(event) => setAcknowledged(event.target.checked)}
          />
          <span>{t('profile.lifecycle.acknowledge')}</span>
        </label>
      </div>
    </Dialog>
  );
}
